/**
 * V4RunsDropdown — Phase 7a-2.
 *
 * Header-mounted dropdown that lists the most recent N workflow runs
 * (newest first). Click a row → host's `onSelectRun(runId)` fires.
 *
 * Lazy-fetched: only hits api.getWorkflowRuns when the dropdown is
 * opened. Re-fetches every open so freshly-started runs (from the Run
 * button) show up without manual refresh. Failures degrade gracefully
 * to an inline error message in the popover.
 *
 * Composition mirrors V4WorkflowSwitcher: trigger button + popover
 * with search-less list (runs are time-ordered; search is rarely
 * useful at <30 entries). Host owns the {workflowId, lastRunId} state.
 *
 * The "last run started" badge on the trigger flashes briefly after a
 * fresh run kicks off — gives the operator a visual confirmation that
 * the Run button worked without needing to open the dropdown.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Loader2, History } from 'lucide-react';
import * as api from '../../services/api';

const POPOVER_WIDTH = 320;
const MAX_RUNS = 12;

// Status → pill colors. Matches the platform's V2 Pill semantics so
// run states read identically across pages.
const STATUS_TONE = {
  COMPLETED:        { bg: '#d1fae5', fg: '#065f46', label: 'Completed' },
  COMPLETED_WITH_ERRORS: { bg: '#fef3c7', fg: '#92400e', label: 'Partial' },
  RUNNING:          { bg: '#dbeafe', fg: '#1e40af', label: 'Running' },
  PAUSED:           { bg: '#e5e7eb', fg: '#475569', label: 'Paused' },
  PENDING_APPROVAL: { bg: '#ede9fe', fg: '#5b21b6', label: 'Awaiting approval' },
  FAILED:           { bg: '#fee2e2', fg: '#991b1b', label: 'Failed' },
  TERMINATED:       { bg: '#fee2e2', fg: '#991b1b', label: 'Terminated' },
  TIMED_OUT:        { bg: '#fef2f2', fg: '#7f1d1d', label: 'Timed out' },
};

function fmtAgo(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '';
  const ms = Date.now() - d.getTime();
  if (ms < 60_000) return 'just now';
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.round(ms / 3_600_000)}h ago`;
  if (ms < 30 * 86_400_000) return `${Math.round(ms / 86_400_000)}d ago`;
  return d.toLocaleDateString();
}

function fmtDuration(startMs, endMs) {
  if (!startMs) return '';
  const end = endMs || Date.now();
  const ms = end - startMs;
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.round(ms / 60_000)}m`;
}

export default function V4RunsDropdown({
  workflowId,
  // Most-recently-started run id (from handleRun). When this changes
  // we refresh the list AND set a brief highlight so the operator sees
  // their fresh run appear at the top.
  freshRunId,
  // Click handler — host opens the 7a-3 detail panel for the run.
  onSelectRun,
}) {
  const [open, setOpen] = useState(false);
  const [runs, setRuns] = useState(null);   /* null = not loaded */
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (!popoverRef.current) return;
      if (popoverRef.current.contains(e.target)) return;
      if (triggerRef.current && triggerRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Fetch on open + every freshRunId change (auto-refresh on a fresh run).
  useEffect(() => {
    if (!workflowId) return undefined;
    if (!open && !freshRunId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.getWorkflowRuns(workflowId)
      .then((res) => {
        if (cancelled) return;
        const payload = (res && 'data' in res) ? res.data : res;
        const list = Array.isArray(payload)
          ? payload
          : (Array.isArray(payload?.items) ? payload.items : []);
        // Sort newest-first. The BE typically returns this order but
        // be defensive — never trust order for time-sensitive UI.
        const sorted = list.slice().sort((a, b) => {
          const ta = new Date(a.startedAt || a.createdAt || 0).getTime();
          const tb = new Date(b.startedAt || b.createdAt || 0).getTime();
          return tb - ta;
        });
        setRuns(sorted);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.message || String(e));
        setRuns([]);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, workflowId, freshRunId]);

  // Latest fresh-run tag (for the highlight ring on the just-started row).
  const freshHighlightId = freshRunId;
  const visible = useMemo(() => (runs || []).slice(0, MAX_RUNS), [runs]);
  const hasRuns = (runs || []).length > 0;

  return (
    <div style={{ position: 'relative' }}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={!workflowId}
        title="Recent runs"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          padding: '5px 10px',
          fontSize: 11, fontWeight: 500,
          color: '#0f172a',
          background: '#ffffff',
          border: '0.5px solid #cbd5e1',
          borderRadius: 6,
          cursor: workflowId ? 'pointer' : 'not-allowed',
          opacity: workflowId ? 1 : 0.5,
          fontFamily: 'inherit',
        }}
      >
        <History size={11} aria-hidden="true" />
        Runs
        <ChevronDown size={11} aria-hidden="true" />
      </button>
      {open && (
        <div
          ref={popoverRef}
          role="region"
          aria-label="Recent workflow runs"
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            right: 0,
            width: POPOVER_WIDTH,
            maxHeight: 420,
            background: '#ffffff',
            border: '0.5px solid #e2e8f0',
            borderRadius: 8,
            boxShadow: '0 14px 36px -10px rgba(15, 23, 42, 0.22), 0 3px 8px -2px rgba(15, 23, 42, 0.10)',
            zIndex: 20,
            display: 'flex', flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '10px 12px',
            borderBottom: '0.5px solid #e2e8f0',
            fontSize: 11, fontWeight: 700, color: '#0f172a',
            textTransform: 'uppercase', letterSpacing: 0.4,
          }}>
            <span>Recent Runs</span>
            {loading && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#64748b', fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}>
                <Loader2 size={11} className="v4-spin" aria-hidden="true" /> Loading
              </span>
            )}
          </div>
          {/* Body */}
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {error && (
              <div style={{ padding: 16, fontSize: 12, color: '#991b1b' }}>
                Couldn&apos;t load runs: {error}
              </div>
            )}
            {!error && runs !== null && !hasRuns && !loading && (
              <div style={{ padding: 24, textAlign: 'center', fontSize: 12, color: '#64748b' }}>
                No runs yet. Click <strong>Run</strong> to start the first one.
              </div>
            )}
            {!error && hasRuns && (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {visible.map((r) => {
                  const status = (r.status || '').toUpperCase();
                  const tone = STATUS_TONE[status] || { bg: '#f1f5f9', fg: '#475569', label: status || 'Unknown' };
                  const isFresh = freshHighlightId && r.id === freshHighlightId;
                  return (
                    <li key={r.id} style={{ borderBottom: '0.5px solid #f1f5f9' }}>
                      <button
                        type="button"
                        onClick={() => { if (typeof onSelectRun === 'function') onSelectRun(r.id); setOpen(false); }}
                        style={{
                          display: 'flex', flexDirection: 'column', gap: 4,
                          width: '100%', padding: '8px 12px',
                          background: isFresh ? '#ecfeff' : '#ffffff',
                          border: 'none',
                          borderLeft: isFresh ? '2px solid #0891b2' : '2px solid transparent',
                          textAlign: 'left',
                          cursor: 'pointer',
                          fontFamily: 'inherit',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{
                            padding: '1px 6px', borderRadius: 999,
                            fontSize: 9.5, fontWeight: 700,
                            color: tone.fg, background: tone.bg,
                            textTransform: 'uppercase', letterSpacing: 0.3,
                          }}>{tone.label}</span>
                          <code style={{
                            fontSize: 10, color: '#64748b',
                            fontFamily: 'SFMono-Regular, Menlo, monospace',
                          }}>
                            {String(r.id || '').slice(0, 8)}
                          </code>
                          <span style={{ flex: 1 }} />
                          <span style={{ fontSize: 10, color: '#94a3b8' }}>
                            {fmtAgo(r.startedAt || r.createdAt)}
                          </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 10.5, color: '#475569' }}>
                          {r.startedAt && (
                            <span>
                              {fmtDuration(new Date(r.startedAt).getTime(), r.completedAt ? new Date(r.completedAt).getTime() : null)}
                            </span>
                          )}
                          {r.triggeredBy && <span>· {r.triggeredBy}</span>}
                          {r.errorMessage && (
                            <span style={{ color: '#991b1b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              · {r.errorMessage}
                            </span>
                          )}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
